import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, RotateCcw } from 'lucide-react';
import {
  DEFAULT_PURCHASE_LEDGER_TEMPLATE,
  PURCHASE_LEDGER_COLUMNS,
  PURCHASE_LEDGER_COPY,
  PURCHASE_LEDGER_LIMITS,
  ROLES,
  type PurchaseLedgerColumnKey,
  type PurchaseLedgerTemplate,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Field, FieldGroup, Input, Spinner, Switch, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { useRole } from '@/store/auth';

interface TemplateInfo {
  template: PurchaseLedgerTemplate;
  canEditTemplate: boolean;
}

/**
 * 구매대장 양식 설정 — 명세서 §5.29 (v1.20). 원장 전용.
 * 원마다 쓰는 서식이 달라 제목·열 구성·열 이름·결재란·하단 문구를 여기서 바꾼다.
 * 저장 즉시 대장 PDF에 반영되고, 정산 패키지(§5.22)의 월별 대장도 같은 양식을 쓴다.
 */
export function PurchaseTemplatePage() {
  const queryClient = useQueryClient();
  const isDirector = useRole() === ROLES.DIRECTOR;
  const [draft, setDraft] = useState<PurchaseLedgerTemplate | null>(null);
  const [signatureText, setSignatureText] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery({
    queryKey: ['purchases', 'template'],
    queryFn: () => apiData<TemplateInfo>('/purchases/template'),
  });

  useEffect(() => {
    if (info.data && !draft) {
      setDraft(info.data.template);
      setSignatureText(info.data.template.signatures.join(', '));
    }
  }, [info.data, draft]);

  const save = useMutation({
    mutationFn: (template: PurchaseLedgerTemplate) =>
      apiData<PurchaseLedgerTemplate>('/purchases/template', { method: 'PUT', body: template }),
    onSuccess: (saved) => {
      setDraft(saved);
      setSignatureText(saved.signatures.join(', '));
      setMessage('양식을 저장했어요. 다음 PDF부터 이 양식으로 나와요.');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['purchases', 'template'] });
    },
    onError: (caught) => {
      setMessage(null);
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.');
    },
  });

  if (!draft) {
    return (
      <Screen>
        <AppBar title="구매대장 양식" back />
        {info.isError ? <p className="p-4 text-sm text-notice">양식을 불러오지 못했어요.</p> : <Spinner />}
      </Screen>
    );
  }

  const enabled = draft.columns;
  const disabled = PURCHASE_LEDGER_COLUMNS.map((c) => c.key).filter((key) => !enabled.includes(key));

  const move = (key: PurchaseLedgerColumnKey, delta: number) => {
    const index = enabled.indexOf(key);
    const target = index + delta;
    if (index === -1 || target < 0 || target >= enabled.length) return;
    const next = [...enabled];
    [next[index], next[target]] = [next[target], next[index]];
    setDraft({ ...draft, columns: next });
  };

  const toggle = (key: PurchaseLedgerColumnKey, on: boolean) => {
    if (on) {
      if (enabled.includes(key)) return;
      setDraft({ ...draft, columns: [...enabled, key] });
    } else {
      if (enabled.length <= 1) {
        setError('열은 하나 이상 남겨 주세요.');
        return;
      }
      setDraft({ ...draft, columns: enabled.filter((k) => k !== key) });
    }
  };

  const setLabel = (key: PurchaseLedgerColumnKey, label: string) => {
    const labels = { ...draft.labels };
    if (label.trim()) labels[key] = label;
    else delete labels[key];
    setDraft({ ...draft, labels });
  };

  const submit = () => {
    const signatures = signatureText
      .split(/[,，/·]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, PURCHASE_LEDGER_LIMITS.signaturesMax);
    save.mutate({ ...draft, signatures });
  };

  // 렌더 함수로 둔다 — 컴포넌트를 안에서 새로 만들면 렌더마다 타입이 바뀌어 입력 포커스가 날아간다
  const renderColumnRow = (columnKey: PurchaseLedgerColumnKey, on: boolean) => {
    const catalog = PURCHASE_LEDGER_COLUMNS.find((c) => c.key === columnKey);
    return (
      <div key={columnKey} className="flex items-center gap-2 py-1.5">
        <Switch checked={on} onChange={(next) => toggle(columnKey, next)} label={catalog?.label} />
        <div className="min-w-0 flex-1">
          <Input
            value={draft.labels[columnKey] ?? ''}
            placeholder={catalog?.label}
            maxLength={PURCHASE_LEDGER_LIMITS.labelMax}
            disabled={!on || !isDirector}
            onChange={(e) => setLabel(columnKey, e.target.value)}
          />
        </div>
        {on && (
          <>
            <button
              type="button"
              aria-label="위로"
              className="tap grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-primary-strong disabled:opacity-40"
              disabled={enabled.indexOf(columnKey) === 0}
              onClick={() => move(columnKey, -1)}
            >
              <ArrowUp size={16} />
            </button>
            <button
              type="button"
              aria-label="아래로"
              className="tap grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-primary-strong disabled:opacity-40"
              disabled={enabled.indexOf(columnKey) === enabled.length - 1}
              onClick={() => move(columnKey, 1)}
            >
              <ArrowDown size={16} />
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <Screen>
      <AppBar title="구매대장 양식" back />

      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-ink-sub">{PURCHASE_LEDGER_COPY.templateHint}</p>
        {!isDirector && (
          <p className="rounded-sm bg-accent-soft px-3 py-2 text-sm">양식 변경은 원장님만 할 수 있어요. 지금 양식을 확인만 할 수 있어요.</p>
        )}

        <Card>
          <Field label="대장 제목" required>
            <Input
              value={draft.title}
              maxLength={PURCHASE_LEDGER_LIMITS.titleMax}
              disabled={!isDirector}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Field>
          <Field label="결재란 (쉼표로 구분)" hint="비우면 결재란 없이 출력돼요. 예: 담당, 원감, 원장">
            <Input
              value={signatureText}
              placeholder="담당, 원장"
              disabled={!isDirector}
              onChange={(e) => setSignatureText(e.target.value)}
            />
          </Field>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm font-medium">합계 행 표시</span>
            <Switch checked={draft.showTotals} onChange={(next) => setDraft({ ...draft, showTotals: next })} label="합계 행 표시" />
          </div>
        </Card>

        <Card>
          <FieldGroup label="열 구성과 순서" hint="켜진 열만 순서대로 나와요. 이름 칸을 비우면 기본 이름을 써요.">
            <div className="flex flex-col divide-y divide-line">
              {enabled.map((key) => renderColumnRow(key, true))}
              {disabled.map((key) => renderColumnRow(key, false))}
            </div>
          </FieldGroup>
        </Card>

        <Card>
          <Field label="하단 문구" hint="비우면 새싹돋보기 기본 문구가 들어가요.">
            <Textarea
              rows={2}
              value={draft.footnote}
              maxLength={PURCHASE_LEDGER_LIMITS.footnoteMax}
              placeholder="예: 본 대장은 증빙 원본과 함께 5년간 보관합니다."
              disabled={!isDirector}
              onChange={(e) => setDraft({ ...draft, footnote: e.target.value })}
            />
          </Field>
        </Card>

        {message && <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">{message}</p>}
        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        {isDirector && (
          <div className="flex gap-2">
            <Button block loading={save.isPending} onClick={submit}>
              양식 저장
            </Button>
            <Button
              variant="outline"
              icon={<RotateCcw size={16} />}
              onClick={() => {
                setDraft({
                  ...DEFAULT_PURCHASE_LEDGER_TEMPLATE,
                  columns: [...DEFAULT_PURCHASE_LEDGER_TEMPLATE.columns],
                  labels: {},
                  signatures: [...DEFAULT_PURCHASE_LEDGER_TEMPLATE.signatures],
                });
                setSignatureText(DEFAULT_PURCHASE_LEDGER_TEMPLATE.signatures.join(', '));
                setMessage(null);
              }}
            >
              기본 양식
            </Button>
          </div>
        )}
      </div>
    </Screen>
  );
}
